import { AggregateCartesianChartDataConfig } from '@app/data/interface/widget-config';
import { SeriesOption } from 'echarts';
import {
  buildSourceIdentifierLookup,
  getSourceIdentifierFromDataPoint,
} from '@app/client/clients/wastemanagement/taiao-dashboard-layout/widget-helpers/vegetation-widgets';
import { WidgetNumberUtils } from '@app/client/clients/wastemanagement/taiao-dashboard-layout/widget-helpers/widget-number-utils';
import { buildAllowedIdentifierSet } from './source-filter-utils';

interface EntityLookupEntry {
  key: string;
  label: string;
}

/**
 * Information about a bird observation record.
 */
type RecordInfo = {
  siteId: string;
  siteSourceIdentifier: string | null;
  speciesKey: string;
  displayLabel: string;
  canonicalIndex: number | null;
  value: number;
};

/**
 * Information about a species series used to build chart series and legend labels.
 */
type SpeciesInfo = {
  key: string;
  label: string;
  priorityIndex: number | null;
  synthetic?: boolean;
};

/**
 * Find an existing SpeciesInfo by normalized label.
 */
const findExistingByNormalizedLabel = (normalized: string, infos: SpeciesInfo[]) =>
  infos.find((info) => normaliseLabel(info.label) === normalized);

/**
 * Try to find a full label from an entity lookup by normalized label.
 */
const findFullLabelInEntityLookup = (
  normalized: string,
  entityLookup: Record<string, EntityLookupEntry>
): string | null => {
  for (const ent of Object.values(entityLookup)) {
    if (ent && ent.label && normaliseLabel(ent.label) === normalized) {
      return ent.label;
    }
  }
  return null;
};

export const prioritySpeciesLabels = ['Kererū | Wood Pigeon', 'Tūī', 'Pipit'];

/**
 * Default ordered list of site identifiers for terrestrial bird observations.
 * This list is used to maintain a consistent order of sites in the chart.
 */
export const DEFAULT_SITE_ORDER: string[] = [
  'arl_aa31',
  'arl_ab29',
  'arl_ab33',
  'arl_ab50',
  'arl_ac46',
  'arl_ac51',
  'arl_ad33',
  'arl_ad46',
  'arl_ad49',
  'arl_ae35',
  'arl_ae38',
  'arl_ae43',
  'arl_ae50',
  'arl_af41',
  'arl_af45',
  'arl_af48',
  'arl_ag37',
  'arl_ag40',
  'arl_ah39',
  'arl_j10',
  'arl_j12',
  'arl_k10',
  'arl_k9',
  'arl_n12',
  'arl_n9',
  'arl_o7',
  'arl_p10',
  'arl_p11',
  'arl_p12',
  'arl_p6',
  'arl_p7',
  'arl_q10',
  'arl_q11',
  'arl_q12',
  'arl_q13',
  'arl_q14',
  'arl_q15',
  'arl_q5',
  'arl_q7',
  'arl_q8',
  'arl_q9',
  'arl_r10',
  'arl_r11',
  'arl_r12',
  'arl_r13',
  'arl_r15',
  'arl_r16',
  'arl_r8',
  'arl_r9',
  'arl_s10',
  'arl_s11',
  'arl_s12',
  'arl_s13',
  'arl_s15',
  'arl_s17',
  'arl_s8',
  'arl_s9',
  'arl_t10',
  'arl_t13',
  'arl_u11',
  'arl_u13',
  'arl_u15',
  'arl_u9',
  'arl_v14',
  'arl_v16',
  'arl_v18',
  'arl_w17',
  'arl_w19',
  'arl_w21',
  'arl_x15',
  'arl_x17',
  'arl_y16',
  'arl_z29',
];

/**
 * Normalizes a label by removing diacritics and converting to lowercase.
 * @param value - The label to normalize.
 * @returns The normalized label.
 */
const normaliseLabel = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

/**
 * Registers an entity in the lookup table.
 * @param lookup - The entity lookup table.
 * @param entity - The entity to register.
 * @returns
 */
const registerEntity = (lookup: Record<string, EntityLookupEntry>, entity: any) => {
  if (!entity) return;
  if (entity.id == null) return;

  const entity_unique_id = entity.unique_id;

  if (!entity.name) {
    console.warn(
      `[terrestrial-bird-widgets] entity missing 'name' for id=${entity.id}, unique_id=${entity_unique_id ?? 'n/a'}`,
      entity
    );
  }

  lookup[String(entity.id)] = {
    key: entity_unique_id,
    label: entity.name,
  };
};

/**
 * Consumes a list of entities and registers them in the lookup.
 * @param lookup - The entity lookup table.
 * @param entities - The entities to register.
 * @returns
 */
const consumeEntities = (lookup: Record<string, EntityLookupEntry>, entities: any) => {
  if (!entities) return;
  if (Array.isArray(entities)) {
    entities.forEach((entity) => registerEntity(lookup, entity));
    return;
  }
  if (typeof entities === 'object') {
    Object.values(entities).forEach((entity) => registerEntity(lookup, entity));
  }
};

/**
 * Builds an entity lookup table from the series data.
 * @param seriesData - The series data containing entities.
 * @returns A record mapping entity IDs to their lookup entries.
 */
const buildEntityLookup = (seriesData: any): Record<string, EntityLookupEntry> => {
  const lookup: Record<string, EntityLookupEntry> = {};

  consumeEntities(lookup, seriesData?.entities);
  consumeEntities(lookup, seriesData?.observationEntities);

  if (Array.isArray(seriesData?.environmentalDataSources)) {
    seriesData.environmentalDataSources.forEach((source: any) => {
      consumeEntities(lookup, source?.entities);
      consumeEntities(lookup, source?.observationEntities);
    });
  }

  return lookup;
};

/**
 * Extracts information from a bird observation record.
 * @param record - The observation record to extract information from.
 * @param sourceLookup - A lookup table for source identifiers.
 * @param idToLabelMap - A map of entity IDs to their labels.
 * @param entityLookup - A lookup table for entities.
 * @param priorityCanonicalLabel - A map of priority species labels.
 * @returns The extracted record information or null if extraction fails.
 */
const getRecordInfo = (
  record: any,
  sourceLookup: any,
  idToLabelMap: any,
  entityLookup: Record<string, EntityLookupEntry>,
  priorityCanonicalLabel: Map<string, { label: string; index: number }>,
  sourceIdToIdentifier: Map<string, string>
): RecordInfo | null => {
  if (!record || typeof record !== 'object') return null;

  const siteId = getSourceIdentifierFromDataPoint(record, sourceLookup, idToLabelMap);
  if (!siteId) return null;

  const siteLabel = typeof siteId === 'string' ? siteId.trim() : String(siteId);
  if (!siteLabel) return null;

  const observationId = record?.observation_entity_id;
  const entityInfo = observationId != null ? entityLookup[String(observationId)] : undefined;
  const speciesKey = entityInfo?.key ?? (observationId != null ? String(observationId) : 'unknown-species');
  const rawLabel = entityInfo?.label ?? 'Unknown species';
  const normalisedLabel = normaliseLabel(rawLabel);
  const canonicalInfo = priorityCanonicalLabel.get(normalisedLabel);
  const displayLabel = canonicalInfo?.label ?? rawLabel;
  const value = WidgetNumberUtils.toFiniteNumber(record?.avg) ?? 0;
  const sourceIdentifierRaw =
    record?.environmental_data_source_id != null
      ? sourceIdToIdentifier.get(String(record.environmental_data_source_id))
      : null;
  const siteSourceIdentifier =
    typeof sourceIdentifierRaw === 'string' && sourceIdentifierRaw.trim().length ? sourceIdentifierRaw.trim() : null;

  return {
    siteId: siteLabel,
    siteSourceIdentifier,
    speciesKey,
    displayLabel,
    canonicalIndex: canonicalInfo ? canonicalInfo.index : null,
    value,
  };
};

/**
 * Ensures that a site bucket is present in the relevant maps.
 * @param perSite - A map of site IDs to their respective buckets.
 * @param siteOrder - An array to maintain the order of sites.
 * @param siteId - The ID of the site to ensure.
 * @returns The bucket for the specified site.
 */
const ensureSiteBucket = (perSite: Map<string, Map<string, number>>, siteOrder: string[], siteId: string) => {
  let bucket = perSite.get(siteId);
  if (!bucket) {
    bucket = new Map<string, number>();
    perSite.set(siteId, bucket);
    siteOrder.push(siteId);
  }
  return bucket;
};

/**
 * Ensures that a species label is present in the relevant maps.
 * @param speciesKeyToLabel - A map of species keys to their labels.
 * @param speciesOrder - An array to maintain the order of species.
 * @param speciesKeyToPriorityIndex - A map of species keys to their priority indices.
 * @param speciesKey - The key of the species to ensure.
 * @param displayLabel - The display label for the species.
 * @param canonicalIndex - The canonical index for the species.
 */
const ensureSpeciesLabelPresent = (
  speciesKeyToLabel: Map<string, string>,
  speciesOrder: string[],
  speciesKeyToPriorityIndex: Map<string, number | null>,
  speciesKey: string,
  displayLabel: string,
  canonicalIndex: number | null
) => {
  if (!speciesKeyToLabel.has(speciesKey)) {
    speciesKeyToLabel.set(speciesKey, displayLabel);
    speciesOrder.push(speciesKey);
    speciesKeyToPriorityIndex.set(speciesKey, canonicalIndex);
  }
};

/**
 * Adds a value to a species bucket.
 * @param bucket - The bucket to add the value to.
 * @param speciesKey - The key of the species to add the value for.
 * @param value - The value to add.
 * @returns The updated bucket.
 */
const addValueToBucket = (bucket: Map<string, number>, speciesKey: string, value: number) => {
  if (value === 0) return;
  bucket.set(speciesKey, (bucket.get(speciesKey) ?? 0) + value);
};

/**
 * Build the final site order from the canonical DEFAULT_SITE_ORDER, preserving
 * canonical ordering but only including sites that have data present in
 * `perSite`. The function uses the identifier<->label maps (populated from
 * environmentalDataSources and observed site labels) to reconcile canonical
 * identifiers with the site labels used in the chart data.
 *
 * @param siteOrder - The initial site order derived from data.
 * @param perSite - A map of site labels to their data buckets.
 * @param identifierToLabel - A map of site identifiers to their labels.
 * @param labelToIdentifier - A map of site labels to their identifiers.
 * @param canonicalList - The canonical list of site identifiers to use for ordering.
 * @returns The final ordered list of site labels.
 */
const buildFinalSiteOrderFromCanonical = (
  siteOrder: string[],
  perSite: Map<string, Map<string, number>>,
  identifierToLabel: Map<string, string>,
  labelToIdentifier: Map<string, string>,
  canonicalList: string[] = DEFAULT_SITE_ORDER
): string[] => {
  if (!siteOrder.length) return [];

  const canonicalIdsWithData = new Set<string>();
  siteOrder.forEach((siteLabel) => {
    const identifier = labelToIdentifier.get(siteLabel);
    if (identifier) canonicalIdsWithData.add(String(identifier).toLowerCase());
  });

  return canonicalList.reduce<string[]>((acc, canonicalId) => {
    const normalizedIdentifier = String(canonicalId).toLowerCase();
    if (!canonicalIdsWithData.has(normalizedIdentifier)) return acc;

    const labelFromIdentifier = identifierToLabel.get(normalizedIdentifier);
    if (labelFromIdentifier && perSite.has(labelFromIdentifier)) {
      acc.push(labelFromIdentifier);
      return acc;
    }

    const fallbackLabel = siteOrder.find(
      (siteLabel) => String(labelToIdentifier.get(siteLabel) ?? '').toLowerCase() === normalizedIdentifier
    );
    if (fallbackLabel && perSite.has(fallbackLabel)) acc.push(fallbackLabel);

    return acc;
  }, []);
};

/**
 * Maps terrestrial bird species data to a format suitable for the charting library.
 *
 * Constructs a stacked bar chart series from the provided bird species observation data.
 * @param _keyedBySourceId - A map of source IDs to their keyed data.
 * @param idToLabelMap - A map of entity IDs to their labels.
 * @param xAxisData - The data for the X axis.
 * @param legendLabels - The labels for the legend.
 * @param dataSource - The data source for the chart.
 * @param seriesData - The series data containing the bird species information.
 * @returns The mapped data for the chart.
 */
export const mapTerrestrialBirdSpeciesData: AggregateCartesianChartDataConfig['mapDataToSeries'] = (
  _keyedBySourceId,
  idToLabelMap,
  xAxisData,
  legendLabels,
  dataSource,
  seriesData,
  selectedSources
) => {
  const records = Array.isArray(seriesData?.data) ? seriesData.data : [];
  if (!records.length || !dataSource) return [];

  const allowedIdentifiers = buildAllowedIdentifierSet(selectedSources);

  const sourceLookup = buildSourceIdentifierLookup(seriesData);
  const entityLookup = buildEntityLookup(seriesData);
  const siteOrder: string[] = [];
  const speciesOrder: string[] = [];
  const speciesKeyToLabel = new Map<string, string>();
  const speciesKeyToPriorityIndex = new Map<string, number | null>();
  const perSite = new Map<string, Map<string, number>>();
  const priorityCanonicalLabel = new Map(
    prioritySpeciesLabels.map((label, index) => [normaliseLabel(label), { label, index }])
  );

  const environmentalSources = Array.isArray(seriesData?.environmentalDataSources)
    ? seriesData.environmentalDataSources
    : [];
  const sourceIdToIdentifier = new Map<string, string>();
  const identifierToLabel = new Map<string, string>();
  const labelToIdentifier = new Map<string, string>();

  environmentalSources.forEach((source: any) => {
    if (!source) return;

    const rawIdentifier = typeof source?.source_identifier === 'string' ? source.source_identifier.trim() : '';
    if (!rawIdentifier) return;

    const normalizedIdentifier = rawIdentifier.toLowerCase();

    if (source.id != null) {
      sourceIdToIdentifier.set(String(source.id), rawIdentifier);
    }

    const label = source.human_readable_name;

    if (!identifierToLabel.has(normalizedIdentifier)) {
      identifierToLabel.set(normalizedIdentifier, label);
    }

    if (!labelToIdentifier.has(label)) {
      labelToIdentifier.set(label, normalizedIdentifier);
    }
  });

  for (const record of records) {
    const info = getRecordInfo(
      record,
      sourceLookup,
      idToLabelMap,
      entityLookup,
      priorityCanonicalLabel,
      sourceIdToIdentifier
    );
    if (!info) continue;

    const { siteId, siteSourceIdentifier, speciesKey, displayLabel, canonicalIndex, value } = info;

    const normalizedSourceIdentifier =
      typeof siteSourceIdentifier === 'string' && siteSourceIdentifier.length
        ? siteSourceIdentifier.toLowerCase()
        : labelToIdentifier.get(siteId) ?? null;

    if (normalizedSourceIdentifier) {
      identifierToLabel.set(normalizedSourceIdentifier, siteId);
      labelToIdentifier.set(siteId, normalizedSourceIdentifier);
    }

    if (allowedIdentifiers && (!normalizedSourceIdentifier || !allowedIdentifiers.has(normalizedSourceIdentifier))) {
      continue;
    }

    const bucket = ensureSiteBucket(perSite, siteOrder, siteId);
    ensureSpeciesLabelPresent(
      speciesKeyToLabel,
      speciesOrder,
      speciesKeyToPriorityIndex,
      speciesKey,
      displayLabel,
      canonicalIndex
    );
    if (!bucket.has(speciesKey)) bucket.set(speciesKey, 0);
    addValueToBucket(bucket, speciesKey, value);
  }

  if (!siteOrder.length) return [];

  const speciesInfos: SpeciesInfo[] = speciesOrder.map((key) => ({
    key,
    label: speciesKeyToLabel.get(key) ?? key,
    priorityIndex: speciesKeyToPriorityIndex.get(key) ?? null,
  }));

  prioritySpeciesLabels.forEach((priorityLabel, index) => {
    const normalisedPriority = normaliseLabel(priorityLabel);
    const existing = findExistingByNormalizedLabel(normalisedPriority, speciesInfos);
    if (existing) {
      existing.priorityIndex = index;
      return;
    }

    const fullLabel = findFullLabelInEntityLookup(normalisedPriority, entityLookup) ?? priorityLabel;
    speciesInfos.push({
      key: `priority-${index}`,
      label: fullLabel,
      priorityIndex: index,
      synthetic: true,
    });
  });

  speciesInfos.sort((a, b) => {
    const priA = a.priorityIndex ?? Number.POSITIVE_INFINITY;
    const priB = b.priorityIndex ?? Number.POSITIVE_INFINITY;
    if (priA !== priB) return priA - priB;
    return a.label.localeCompare(b.label);
  });

  if (!siteOrder.length) return [];

  const finalSiteOrder = siteOrder.sort();

  if (!finalSiteOrder.length) return [];

  xAxisData.length = 0;
  xAxisData.push(...finalSiteOrder);

  legendLabels.length = 0;
  const legendSeen = new Set<string>();

  speciesInfos.forEach((info) => {
    if (info.priorityIndex != null) {
      if (!legendSeen.has(info.label)) {
        legendLabels.push(info.label);
        legendSeen.add(info.label);
      }
    }
  });

  speciesInfos.forEach((info) => {
    if (info.priorityIndex == null) {
      if (!legendSeen.has(info.label)) {
        legendLabels.push(info.label);
        legendSeen.add(info.label);
      }
    }
  });

  const stackedSeries: SeriesOption[] = speciesInfos.map((info) => {
    const data = finalSiteOrder.map((site) => {
      if (info.synthetic) return 0;
      const bucket = perSite.get(site);
      if (!bucket) return 0;
      return bucket.get(info.key) ?? 0;
    });

    return {
      name: info.label,
      type: 'bar',
      stack: 'total',
      emphasis: { focus: 'series' },
      data,
    } as SeriesOption;
  });

  return stackedSeries;
};
